import facebookLogo from '@/../../public/storage/social-media/facebook_logo.png';
import linkedinLogo from '@/../../public/storage/social-media/linkedin_logo.png';
import whatsappLogo from '@/../../public/storage/social-media/whatsapp_logo.png';
import xLogoBlack from '@/../../public/storage/social-media/x_logo_black.png';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Check, Copy, Mail, Share2 } from 'lucide-react';
import { useState } from 'react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  EmailShareButton,
} from 'react-share';
import { toast } from 'sonner';

const ShareOption = ({
  as: As = 'button',
  icon,
  label,
  ...props
}: {
  as?: React.ElementType;
  icon: React.ReactNode;
  label: string;
  [key: string]: unknown;
}) => (
  <As
    className="group flex flex-col items-center gap-2 rounded-lg p-2 text-center transition-colors hover:bg-accent"
    {...props}
  >
    <span className="inline-flex size-12 items-center justify-center rounded-full bg-muted transition-transform group-hover:scale-105">
      {icon}
    </span>
    <span className="text-xs text-muted-foreground">{label}</span>
  </As>
);

const ShareDialog = ({
  url,
  title,
  content,
}: {
  url: string;
  title: string;
  content: string;
}) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Mobile browsers get the OS share sheet, which lists every app the visitor
  // actually has installed rather than the fixed set of networks below. An
  // AbortError just means the visitor dismissed that sheet, not a failure.
  const handleTriggerClick = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: content, url });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          setOpen(true);
        }
      }
      return;
    }

    setOpen(true);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        onClick={() => void handleTriggerClick()}
      >
        <Share2 size={16} className="mr-2" /> Share
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Share</DialogTitle>
            <DialogDescription>
              Share this page with your network.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-1.5 pl-3">
            <Input
              readOnly
              value={url}
              onFocus={(event) => event.target.select()}
              className="h-8 border-0 bg-transparent p-0 text-sm text-muted-foreground shadow-none focus-visible:ring-0"
            />
            <Button
              type="button"
              size="icon"
              variant="secondary"
              className="size-8 shrink-0"
              aria-label="Copy link"
              onClick={() => void handleCopy()}
            >
              {copied ? (
                <Check size={16} className="text-primary" />
              ) : (
                <Copy size={16} />
              )}
            </Button>
          </div>

          <div className="grid grid-cols-4 gap-1">
            <ShareOption
              as={WhatsappShareButton}
              url={url}
              title={`*${title}*\n\n${content}\n\n`}
              icon={<img src={whatsappLogo} alt="" className="size-6" />}
              label="WhatsApp"
            />
            <ShareOption
              as={FacebookShareButton}
              url={url}
              icon={<img src={facebookLogo} alt="" className="size-6" />}
              label="Facebook"
            />
            <ShareOption
              as={LinkedinShareButton}
              url={url}
              title={title}
              summary={content}
              source="Samuthrika Academy"
              icon={<img src={linkedinLogo} alt="" className="size-6" />}
              label="LinkedIn"
            />
            <ShareOption
              as={TwitterShareButton}
              url={url}
              title={title}
              icon={<img src={xLogoBlack} alt="" className="size-5" />}
              label="X"
            />
            <ShareOption
              as={EmailShareButton}
              url={url}
              subject={title}
              body={content}
              icon={<Mail size={22} strokeWidth={1.5} />}
              label="Email"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareDialog;
